// Centralized, explicit, least-privilege route/role authorization policy.
//
// SECURITY (AppSec High Finding #9 - "No role-based route guard", round 2 closure,
// 2026-09-29): previously this matrix was independently duplicated (and had DRIFTED) across
// AuthGuardService.isRoleAuthorizedForRoute(), a dead/unused copy in app.component.ts (now
// deleted), and a URL-string-based post-hoc copy in header.component.ts's usercheckval() -
// each with a `default: return true` fallthrough for any route/role combination the author
// didn't explicitly think about. This is now the SINGLE source of truth for "may this role
// activate this route" - AuthGuardService.canActivate() is the primary (pre-activation)
// enforcement point; header.component.ts's own post-hoc check remains as a secondary,
// lower-risk-to-touch safety net for the brief transitional window before a role is fully
// resolved (see its own comments) and was NOT migrated to call this module in this round -
// if you change the policy here, check whether that call site needs a matching update too.
//
// Every protected route must have an EXPLICIT entry below - there is no "allow by default"
// for an unrecognized route path OR an unrecognized role value; both fail CLOSED (deny).
export type AppRole = 'Admin' | 'ApproverAdmin' | 'Approver' | 'User' | 'Unauthorized';

// The app's landing/error pages - deliberately allowed for EVERY role, including
// 'Unauthorized' and any unrecognized value. These are the redirect logic's own safety
// valves; denying them would create a redirect loop (e.g. Unauthorized bounced to
// /unauthorized, then denied FROM /unauthorized itself).
const ALWAYS_ALLOWED_ROUTE_PATHS: ReadonlySet<string> = new Set(['unauthorized', 'not-found', '**']);

// Route paths exactly as they appear in Angular's route config (`_route.routeConfig?.path`,
// e.g. with literal ":param" placeholders, NOT a real URL with actual values), grouped by
// the section/feature they belong to. Every entry in app.routes.ts that isn't one of the
// always-allowed paths above must appear in exactly one family here.
const ROUTE_FAMILY_PATHS: Readonly<Record<string, readonly string[]>> = {
  home: ['', 'home', 'home/view-questions/:category/:affiliate/:id'],
  admin: ['admin'],
  reports: ['reports'],
  approvals: ['approvals', 'approvals/view-questions/:category/:affiliate/:id'],
  countermeasures: ['countermeasures', 'countermeasures/view-questions/:category/:affiliate/:id'],
};

// Least-privilege allow-list of route families per role. Mirrors the previously-live
// behavior (see git history / security-assessment memory for the prior rule set) with two
// deliberate tightenings: (1) /reports, /countermeasures and the countermeasure detail route
// previously had NO rule at all (silently allowed for every role via the old
// `default: return true`) - now explicitly allowed for every AUTHENTICATED tier but denied
// for 'Unauthorized'; (2) an unrecognized role string is now denied everywhere instead of
// allowed everywhere.
const ROLE_ALLOWED_ROUTE_FAMILIES: Readonly<Record<AppRole, readonly string[]>> = {
  Admin: ['home', 'admin', 'reports', 'approvals', 'countermeasures'],
  ApproverAdmin: ['home', 'reports', 'approvals', 'countermeasures'],
  Approver: ['home', 'reports', 'approvals', 'countermeasures'],
  User: ['home', 'reports', 'countermeasures'],
  Unauthorized: [],
};

function findRouteFamily(routePath: string): string | null {
  for (const [family, paths] of Object.entries(ROUTE_FAMILY_PATHS)) {
    if ((paths as readonly string[]).includes(routePath)) {
      return family;
    }
  }
  return null;
}

/**
 * Returns whether `role` may activate `routePath`. Fails CLOSED (false) for a route path
 * that isn't cataloged above, or a role value that isn't one of the 5 recognized tiers -
 * there is no silent "allow by default" case.
 */
export function isRoleAllowedForRoute(role: string | null | undefined, routePath: string | null | undefined): boolean {
  const path = routePath ?? '';
  if (ALWAYS_ALLOWED_ROUTE_PATHS.has(path)) {
    return true;
  }
  const family = findRouteFamily(path);
  if (!family) {
    // An unrecognized/uncataloged route path - fail closed rather than silently allowing a
    // route nobody explicitly reviewed access rules for.
    return false;
  }
  const allowedFamilies = ROLE_ALLOWED_ROUTE_FAMILIES[role as AppRole];
  if (!allowedFamilies) {
    return false;
  }
  return allowedFamilies.includes(family);
}
